import React, {useEffect, useState} from 'react';
import {Text, Pressable, VStack, Heading, HStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';

import {StackParamList} from '../../../../navigators';
import InternalTemplate from '../../../../components/templates/internal';
import {SpinnerModal} from '../../../../components/modals';
import useToast from '../../../../hooks';
import {useSalesmanContext} from '../../../../context/salesman';
import {message} from '../../../../utils/messages';
import {IOrderListItem} from '../../../../interfaces/dtos';
import {
  OrderDepartamentEnum,
  OrderStatusEnum,
} from '../../../../constants/enumerations';
import {
  ApprovedIcon,
  CalculatorIcon,
  FactoryIcon,
  FilterCircleFilledIcon,
  FilterCircleIcon,
  RejectedIcon,
  RemoveFilterCircleIcon,
  TruckIcon,
  WaitingIcon,
} from '../../../../components/icons';
import {StyleSheet} from 'react-native';
import {IStatusFilter} from '../../../../context/commons/interfaces';

interface IFilterIconProps {
  filter: IStatusFilter | null;
}

const FilterIcon = ({filter}: IFilterIconProps) => {
  if (filter) {
    return <FilterCircleFilledIcon width={25} style={styles.balanceIcon} />;
  }
  return <FilterCircleIcon width={25} style={styles.balanceIcon} />;
};

type ScreenProps = NativeStackScreenProps<StackParamList, 'SalesmanOrderList'>;

type IOrderListItemProps = {
  key: string;
  item: IOrderListItem;
  position: number;
  handlePressItem: (id: string) => Promise<void>;
};

const OrderListItem = ({
  key,
  item,
  position,
  handlePressItem,
}: IOrderListItemProps) => {
  return (
    <Pressable key={key} onPress={() => handlePressItem(item.id)}>
      <HStack
        background={position % 2 === 0 ? '#F3F9FF' : '#FFFFFF'}
        justifyContent={'space-between'}
        paddingY={3}>
        <VStack maxWidth={'85%'}>
          <Text fontSize={22}>{item.id}</Text>
          <Text color={'#99A2B4'} fontSize={14}>
            {item.customer}
          </Text>
        </VStack>
        <HStack space={2}>
          <WorkflowIcon item={item} />
          <StatusIcon item={item} />
        </HStack>
      </HStack>
    </Pressable>
  );
};

const StatusIcon = ({item}: {item: IOrderListItem}) => {
  if (item.status === OrderStatusEnum.approved) {
    return <ApprovedIcon size={40} />;
  }
  if (item.status === OrderStatusEnum.rejected) {
    return <RejectedIcon size={40} />;
  }
  if (item.status === OrderStatusEnum.waiting) {
    return <WaitingIcon size={40} />;
  }
  return <></>;
};

const WorkflowIcon = ({item}: {item: IOrderListItem}) => {
  if (item.departament === OrderDepartamentEnum.administration) {
    return <CalculatorIcon width={40} height={40} />;
  }
  if (item.departament === OrderDepartamentEnum.logistic) {
    return <TruckIcon width={40} height={40} />;
  }
  if (item.departament === OrderDepartamentEnum.production) {
    return <FactoryIcon width={40} height={40} />;
  }
  return <></>;
};

const OrderListScreen = ({navigation}: ScreenProps) => {
  const {
    fetchSalesmanOrderList,
    orderList,
    salesmanOrderFilter: filter,
    setSalesmanOrderFilter: setFilter,
    setSalesmanClient,
    fetchSalesmanOrderDetail,
  } = useSalesmanContext();
  const [isLoading, setIsLoading] = useState(false);
  const {showServerError} = useToast();

  const handleGoBack = () => {
    navigation.goBack();
  };

  const getOrderList = async () => {
    setIsLoading(true);
    const result = await fetchSalesmanOrderList();
    if (!result) {
      showServerError();
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getOrderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handlePressItem = async (id: string) => {
    setIsLoading(true);
    const result = await fetchSalesmanOrderDetail(id);
    if (result) {
      navigation.navigate('SalesmanOrderSummary');
    } else {
      showServerError();
    }
    setIsLoading(false);
  };

  const handleOnClearFilterPress = () => {
    setFilter(null);
    setSalesmanClient(null);
  };

  const handleOnFilterPress = () => {
    navigation.navigate('SalesmanOrderListFilter');
  };

  return (
    <InternalTemplate
      title={'salesman-order-list-title'}
      contentPaddingX="3"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <VStack justifyContent="flex-start">
        <SpinnerModal show={isLoading} />
        <HStack
          marginBottom={2}
          justifyContent={'space-between'}
          alignItems={'center'}>
          <Heading size={'sm'} color={'#06355E'} marginBottom={5} marginTop={2}>
            {message('salesman-order-list-subtitle')}
          </Heading>
          <HStack paddingBottom={2} paddingRight={3} space={2}>
            {filter && (
              <Pressable onPress={() => handleOnClearFilterPress()}>
                <RemoveFilterCircleIcon width={25} style={styles.balanceIcon} />
              </Pressable>
            )}
            <Pressable onPress={() => handleOnFilterPress()}>
              <FilterIcon filter={filter} />
            </Pressable>
          </HStack>
        </HStack>
        {orderList &&
          orderList.map((item, position) => {
            return (
              <OrderListItem
                key={`key-${position}`}
                item={item}
                position={position}
                handlePressItem={handlePressItem}
              />
            );
          })}
      </VStack>
    </InternalTemplate>
  );
};

const styles = StyleSheet.create({
  balanceIcon: {
    marginTop: 10,
  },
});

export default OrderListScreen;
